import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { FlowStatus } from "@/interfaces/flow"
import { cn } from "@/lib/utils"
import { formatRelativeTime } from "@/utils/date"
import { Link } from "@tanstack/react-router"
import { AlertTriangle, BarChart2, Edit, MessageSquare, MoreHorizontal, Pause, Play, Trash2 } from "lucide-react"
import { useState } from "react"

export interface FlowListItemProps {
  id: number;
  name: string;
  description?: string;
  status: FlowStatus;
  createdAt: Date | string;
  updatedAt: Date | string;
  lastRun?: Date | null;
  executionCount: number;
  messageCount: number;
  conversionRate?: number;
  href: string;
  onDelete?: (id: number) => void;
}

export function FlowListItem({
  id,
  name,
  description,
  status,
  createdAt,
  updatedAt,
  lastRun,
  executionCount,
  messageCount,
  conversionRate,
  href,
  onDelete,
}: FlowListItemProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const toggleStatus = () => {
    console.log(`Alterando status do fluxo ${id} de ${status} para ${status === FlowStatus.ACTIVE ? "paused" : "active"}`)
  }

  const handleDelete = () => {
    if (onDelete) {
      onDelete(id)
    }
    setIsDeleteDialogOpen(false)
  }

  return (
    <Card className="relative group overflow-hidden transition-all hover:shadow-md">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={href}
                      className="text-lg font-medium hover:text-primary transition-colors line-clamp-2"
                      title={name}
                    >
                      {name}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="top">{name}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <StatusBadge status={status} />
            </div>
            {description && (
              <p className="text-sm text-muted-foreground line-clamp-2" title={description}>
                {description}
              </p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Abrir menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="cursor-pointer" onClick={toggleStatus}>
                {status === FlowStatus.ACTIVE ? (
                  <>
                    <Pause className="mr-2 h-4 w-4" />
                    Pausar
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Ativar
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={href} className="cursor-pointer">
                  <Edit className="mr-2 h-4 w-4" />
                  Editar
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer text-destructive focus:text-destructive"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Execuções</span>
            <div className="flex items-center gap-1 mt-1">
              <Play className="h-3 w-3 text-primary" />
              <span className="font-medium">{executionCount}</span>
            </div>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Mensagens</span>
            <div className="flex items-center gap-1 mt-1">
              <MessageSquare className="h-3 w-3 text-primary" />
              <span className="font-medium">{messageCount}</span>
            </div>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Conversão</span>
            <div className="flex items-center gap-1 mt-1">
              <BarChart2 className="h-3 w-3 text-primary" />
              <span className="font-medium">{conversionRate ? `${conversionRate}%` : "N/A"}</span>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="px-4 py-3 border-t bg-muted/20 flex flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-between sm:items-center">
        <div className="flex flex-col xs:flex-row gap-2 xs:gap-4 text-xs text-muted-foreground">
          <span>
            Criado{" "}
            <time dateTime={`${createdAt}`} className="font-medium">
              {formatRelativeTime(createdAt)}
            </time>
          </span>
          {lastRun && (
            <span>
              Última execução{" "}
              <time dateTime={`${lastRun}`} className="font-medium">
                {formatRelativeTime(lastRun)}
              </time>
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" asChild className="self-end sm:self-auto">
          <Link to={href} className="text-xs">
            Ver detalhes
          </Link>
        </Button>
      </CardFooter>
      <div className="absolute bottom-0 h-1 w-full bg-muted overflow-hidden">
        <div
          className={cn(
            "h-full transition-all",
            status === FlowStatus.ACTIVE
              ? "bg-green-500 w-full"
              : status === FlowStatus.PAUSED
                ? "bg-amber-500 w-full"
                : status === FlowStatus.DRAFT
                  ? "bg-blue-500 w-full"
                  : status === FlowStatus.ERROR
                    ? "bg-red-500 w-full"
                    : "bg-gray-400 w-0",
          )}
        />
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Excluir fluxo
            </AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a excluir o fluxo <span className="font-medium">{name}</span>. Esta ação é irreversível
              e todos os dados associados a este fluxo serão permanentemente removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}

function StatusBadge({ status }: { status: FlowStatus }) {
  switch (status) {
    case FlowStatus.ACTIVE:
      return <Badge variant="default">Ativo</Badge>
    case FlowStatus.PAUSED:
      return (
        <Badge
          variant="outline"
          className="text-amber-500 border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-800"
        >
          Pausado
        </Badge>
      )
    case FlowStatus.DRAFT:
      return (
        <Badge
          variant="outline"
          className="text-blue-500 border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800"
        >
          Rascunho
        </Badge>
      )
    case FlowStatus.ERROR:
      return <Badge variant="destructive">Erro</Badge>
    default:
      return null
  }
}
